
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import MealChip from '@/components/menu/rotational-menus/MealChip.vue';

const MAX_PREVIEW_ITEMS_PER_DAY: number = 6;

@Component<CalendarDateCard>({
	components: {
		MealChip
	}
})
export default class CalendarDateCard extends Vue {
	@Prop({ type: Object, required: true }) private day!: CalendarDay;
	@Prop({ type: Object, required: false, default: null }) private rotationalMenu!: RotationalMenu | null;
	@Prop({ type: Array, required: false, default: () => [] }) private selectedItemIds!: number[];
	@Prop({ type: Boolean, required: false, default: false }) private expanded!: boolean;

	/**
	 * Localized day label combining the day number and weekday, e.g. "1 WED".
	 *
	 * @return {string}
	 */
	private get dayLabel(): string {
		return DateTime.fromISO(this.day.date).setLocale(this.$i18n.locale).toFormat('d ccc').toUpperCase();
	}

	/**
	 * Build the menu groups for the day, each with its full item list and a
	 * preview list. The preview is capped at a single per-day budget shared
	 * across groups (filled in menu order), so a day never shows more than
	 * MAX_PREVIEW_ITEMS_PER_DAY chips regardless of how they split between menus.
	 *
	 * @return {CalendarPreviewMealGroup[]}
	 */
	private get mealGroups(): CalendarPreviewMealGroup[] {
		if (!this.rotationalMenu) {
			return [];
		}
		let remaining: number = MAX_PREVIEW_ITEMS_PER_DAY;
		return this.rotationalMenu.menus.map((menu: Menu) => {
			const items: MenuItem[] = [...menu.items, ...menu.sections.flatMap((section: MenuSection) => section.items)];
			const previewItems: MenuItem[] = items.slice(0, remaining);
			remaining -= previewItems.length;
			return {
				id: menu.id,
				name: this.$options.filters!.localize(menu, 'name'),
				items,
				previewItems
			};
		}).filter((group: CalendarPreviewMealGroup) => group.items.length);
	}

	/**
	 * Menu groups that have at least one preview item, so a group whose budget
	 * ran out doesn't render an orphaned name with no chips.
	 *
	 * @return {CalendarPreviewMealGroup[]}
	 */
	private get previewGroups(): CalendarPreviewMealGroup[] {
		return this.mealGroups.filter((group: CalendarPreviewMealGroup) => group.previewItems.length > 0);
	}

	private get totalMeals(): number {
		return this.mealGroups.reduce((total: number, group: CalendarPreviewMealGroup) => total + group.items.length, 0);
	}

	private get previewCount(): number {
		return this.mealGroups.reduce((total: number, group: CalendarPreviewMealGroup) => total + group.previewItems.length, 0);
	}

	private get hasMeals(): boolean {
		return this.totalMeals > 0;
	}

	/**
	 * Show the see-more button whenever the day has more meals than the card
	 * preview shows, so no meal is ever unreachable.
	 *
	 * @return {boolean}
	 */
	private get showExpand(): boolean {
		return this.totalMeals > this.previewCount;
	}

	private get selectedCount(): number {
		return this.selectedItemIds.length;
	}

	private isSelected(item: MenuItem): boolean {
		return this.selectedItemIds.includes(item.id);
	}
}
